import { isEmpty } from 'lodash';

import { LinkBroken01Icon } from '@/components/icons/LinkBroken01Icon';
import {
  ConfirmationModal,
  ConfirmationModalProps,
} from '@/components/modals/ConfirmationModal/ConfirmationModal';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  MenuItem,
  MenuItemProps,
} from '@/components/poppers/MenuPopper/MenuItem';
import { useModalState } from '@/hooks/useModalState';
import { useReportError } from '@/hooks/useReportError';
import { getIntegrationProviderDisplayName } from '@/modules/assetProviderIntegrations/shared/constants';
import { AssetIntegration_EntityFragment } from '@/modules/assetProviderIntegrations/shared/graphql/assetProviderIntegration.fragments.generated';
import {
  getEntityAssetsIntegrationProvider,
  getIntegrationEntitiesFromEntity,
} from '@/modules/assetProviderIntegrations/shared/utils';

import { useUnlinkIntegrationEntityMutation } from './graphql/UnlinkFromIntegrationMenuItem.generated';

interface UnlinkFromIntegrationMenuItemProps extends Partial<MenuItemProps> {
  entity: AssetIntegration_EntityFragment | null;
  modalProps?: Partial<ConfirmationModalProps>;
}

export function UnlinkFromIntegrationMenuItem({
  entity,
  modalProps = {},
  ...menuItemProps
}: UnlinkFromIntegrationMenuItemProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const [removeIntegrationEntity, { loading }] =
    useUnlinkIntegrationEntityMutation({
      onError: (error) => {
        reportError(`Failed to unlink entity`, error);
        showFeedback(
          'Failed to unlink this entity. Please refresh the page and try again.'
        );
      },
      onCompleted: () => {
        showFeedback('Entity unlinked successfully', { variant: 'success' });
      },
    });

  const handleModalClose: ConfirmationModalProps['onClose'] = async (
    result
  ) => {
    if (result === 'confirm') {
      const integrationEntities = getIntegrationEntitiesFromEntity(entity);

      if (!entity || isEmpty(integrationEntities)) {
        throw new Error(
          'Entity or integration entity not found when trying to unlink from integration.'
        );
      }

      const integrationEntityIds = integrationEntities.map((ie) => ie.id);
      await removeIntegrationEntity({
        variables: {
          entityId: entity.id,
          integrationEntityIds,
        },
      });
    }
  };

  const [{ isModalOpen }, { setModalIsOpen }] = useModalState();

  const integrationProviderName = getEntityAssetsIntegrationProvider(entity);
  if (!integrationProviderName) return null;
  const integrationProviderDisplayName = getIntegrationProviderDisplayName(
    integrationProviderName
  );

  return (
    <>
      <ConfirmationModal
        isOpen={isModalOpen}
        heading={`Are you sure you wish to unlink from ${integrationProviderDisplayName}?`}
        cancelText="Cancel"
        confirmText="Proceed"
        confirmButtonProps={{ variant: 'destructive-primary' }}
        {...modalProps}
        onClose={handleModalClose}
      >
        Existing valuations will be retained but no future valuations will be
        recorded.
      </ConfirmationModal>
      <MenuItem
        variant="destructive"
        icon={<LinkBroken01Icon size={16} />}
        label={`Unlink from ${integrationProviderDisplayName}`}
        onClick={(event) => {
          event.stopPropagation();
          setModalIsOpen(true);
        }}
        loading={loading}
        {...menuItemProps}
      />
    </>
  );
}
